/**
 * Centralized analytics constants for the Champions application.
 * 
 * These constants ensure consistency across all analytics tracking
 * and make it easier to update event names and sources in one place.
 */

/**
 * Analytics event action types.
 */
export const ANALYTICS_ACTIONS = {
  /** User clicked on an element */
  CLICKED: 'clicked',
  
  /** User hovered over an element */
  HOVERED: 'hovered',
  
  /** Form validation failed */
  VALIDATION_FAILED: 'validationFailed',
  
  /** Form was submitted */
  SUBMITTED: 'submitted',
  
  /** Form submission succeeded */
  SUCCEEDED: 'succeeded',
  
  /** Form submission failed */
  FAILED: 'failed',
} as const;

/**
 * Analytics action subject types.
 */
export const ANALYTICS_SUBJECTS = {
  /** Button element */
  BUTTON: 'button',
  
  /** Form element */
  FORM: 'form',
} as const;

/**
 * Analytics source identifiers for Champions sections.
 */
export const CHAMPIONS_ANALYTICS_SOURCES = {
  /** Champions application form */
  APPLICATION_FORM: 'championsApplicationForm',
  
  /** Champions consent form */
  CONSENT_FORM: 'championsConsentForm',
  
  /** Champions hero section */
  HERO_SECTION: 'championsHeroSection',
  
  /** Champions awards section */
  AWARDS_SECTION: 'championsAwardsSection',
  
  /** Champions eligibility section */
  ELIGIBILITY_SECTION: 'championsEligibilitySection',
} as const;

/**
 * Analytics screen/page names.
 */
export const ANALYTICS_SCREENS = {
  /** Champions signup page screen event */
  SIGNUP: 'championsSignupScreen',
  
  /** Champions consent page screen event */
  CONSENT: 'championsConsentScreen',
} as const;

/**
 * Default values for analytics attributes.
 */
export const ANALYTICS_DEFAULTS = {
  /** Default user ID for anonymous users */
  ANONYMOUS_USER: 'anonymous',
  
  /** Default entry point when none is specified */
  DEFAULT_ENTRY_POINT: 'directAccess',
  
  /** Default referrer when none is available */
  DEFAULT_REFERRER: 'none',
} as const;

/**
 * PII (Personally Identifiable Information) fields that should be excluded
 * from analytics tracking to protect user privacy.
 * 
 * These field names are excluded from the filledFields array in form progress
 * and abandonment events. Field counts still include PII fields, but the
 * specific field names are not tracked.
 */
export const PII_FIELDS = [
  // Personal identification
  'firstName',
  'lastName',
  'email',
  'emailId',
  
  // Contact information
  'linkedinUrl',
  'communityProfileUrl',
  
  // Address information
  'street',
  'city',
  'stateProvince',
  'postalCode',
  'country',
  
  // Sensitive personal data
  'birthDate',
  
  // Social media and external URLs that may contain PII
  'primarySocialProfile',
  'otherURL',
  
  // User-generated content that may contain PII
  'referrerName',
  'howHeardEmployee',
] as const;

/**
 * Additional analytics actions for form interactions.
 */
export const FORM_ACTIONS = {
  /** Form was abandoned by user (page unload, navigation away) */
  ABANDONED: 'abandoned',
  
  /** User interaction detected on a form field */
  FIELD_INTERACTED: 'fieldInteracted',
} as const;

/**
 * Type exports for constant values.
 */
export type AnalyticsAction = typeof ANALYTICS_ACTIONS[keyof typeof ANALYTICS_ACTIONS];
export type AnalyticsSubject = typeof ANALYTICS_SUBJECTS[keyof typeof ANALYTICS_SUBJECTS];
export type ChampionsAnalyticsSource = typeof CHAMPIONS_ANALYTICS_SOURCES[keyof typeof CHAMPIONS_ANALYTICS_SOURCES];
export type AnalyticsScreen = typeof ANALYTICS_SCREENS[keyof typeof ANALYTICS_SCREENS];
export type FormAction = typeof FORM_ACTIONS[keyof typeof FORM_ACTIONS];
